import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-totp-prompt.css?inline';
import '../pk-modal/pk-modal.js';
import '../pk-button/pk-button.js';
import '../pk-alert/pk-alert.js';

/**
 * `<pk-totp-prompt>` — TOTP re-authentication modal for destructive admin actions.
 *
 * Composes on top of `<pk-modal>`. Renders a 6-digit code field plus
 * Verify/Cancel action buttons. Fires `verified` (detail: `{ code }`) when
 * a valid 6-digit numeric code is submitted, and `cancelled` when the user
 * dismisses the dialog.
 *
 * Visibility mirrors `<pk-modal>`: toggle the boolean `open` attribute or
 * call the imperative `show()` / `hide()` methods.
 *
 * Attributes:
 *   - `open`          — boolean; shows/hides the prompt.
 *   - `title`         — dialog heading. Default: `Confirm with your authenticator code`.
 *   - `lede`          — paragraph below the heading. Optional.
 *   - `action-label`  — describes the gated action (e.g. `Delete user`). Optional;
 *                       when set, appears in a soft callout below the lede.
 *   - `error`         — error message; renders a `<pk-alert variant="error">` when set.
 *   - `verify-label`  — verify button text. Default `Verify`.
 *   - `cancel-label`  — cancel button text. Default `Cancel`.
 *
 * Events:
 *   - `verified` — `CustomEvent<{ code: string }>` fired when a valid 6-digit
 *                  numeric code is submitted. Consumer is responsible for the
 *                  actual server-side verification + carrying out the action.
 *   - `cancelled` — fired when the modal is dismissed (cancel button, backdrop,
 *                   Escape key, or close X).
 */
export class PkTotpPrompt extends HTMLElement {
    static readonly tagName = 'pk-totp-prompt';

    static get observedAttributes(): string[] {
        return [
            'open', 'title', 'lede', 'action-label', 'error',
            'verify-label', 'cancel-label', 'submitting',
        ];
    }

    private readonly _root: ShadowRoot;
    private _code = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('pk-cancel', this._onModalClose);
    }

    disconnectedCallback(): void {
        this.removeEventListener('pk-cancel', this._onModalClose);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'open' && !this.hasAttribute('open')) {
            // Reset the typed code whenever the modal closes so the next open
            // doesn't show stale state.
            this._code = '';
        }
        this.render();
    }

    /** Programmatically open the prompt. */
    show(): void {
        this.setAttribute('open', '');
    }

    /** Programmatically close the prompt. */
    hide(): void {
        this.removeAttribute('open');
    }

    private readonly _onModalClose = (e: Event): void => {
        // pk-modal v0.4 fires pk-cancel (and pk-modal-close for back-compat).
        // We listen to pk-cancel only to avoid double-firing.
        e.stopPropagation();
        this._emitCancelled();
    };

    private readonly _onInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        // Keep digits only and clamp to 6.
        const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
        this._code = cleaned;
        if (input.value !== cleaned) {
            input.value = cleaned;
        }
        this.render();
    };

    /**
     * Enter-to-submit on the authenticator code input. Triggers the same code
     * path as clicking Verify. preventDefault stops the browser from also
     * submitting a parent `<form>` element wrapping this component. No-ops
     * while the verify round-trip is in flight (`submitting` attribute) or
     * when the code is not yet 6 digits.
     */
    private readonly _onCodeKeydown = (e: KeyboardEvent): void => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (this.hasAttribute('submitting')) return;
        if (!/^\d{6}$/.test(this._code)) return;
        this._onVerifyClick();
    };

    private readonly _onVerifyClick = (): void => {
        if (this.hasAttribute('submitting')) return;
        if (!/^\d{6}$/.test(this._code)) return;
        this.dispatchEvent(new CustomEvent('verified', {
            detail:   { code: this._code },
            bubbles:  true,
            composed: true,
        }));
    };

    private readonly _onCancelClick = (): void => {
        this._emitCancelled();
    };

    private _emitCancelled(): void {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('cancelled', {
            bubbles:  true,
            composed: true,
        }));
    }

    private template(): TemplateResult {
        const isOpen      = this.hasAttribute('open');
        const title       = this.getAttribute('title')        ?? 'Confirm with your authenticator code';
        const lede        = this.getAttribute('lede')         ?? '';
        const actionLabel = this.getAttribute('action-label') ?? '';
        const error       = this.getAttribute('error')        ?? '';
        const verifyLabel = this.getAttribute('verify-label') ?? 'Verify';
        const cancelLabel = this.getAttribute('cancel-label') ?? 'Cancel';
        const submitting  = this.hasAttribute('submitting');
        const canVerify   = /^\d{6}$/.test(this._code) && !submitting;

        return html`
            <style>${styles}</style>
            <pk-modal
                ?open=${isOpen}
                title=${title}
                size="small"
                dismissible="escape"
            >
                ${lede ? html`<p class="lede">${lede}</p>` : ''}

                ${actionLabel ? html`
                    <div class="action-callout">
                        <span class="action-callout-label">Action:</span>
                        <strong class="action-callout-value">${actionLabel}</strong>
                    </div>
                ` : ''}

                ${error ? html`<pk-alert variant="error" class="prompt-error">${error}</pk-alert>` : ''}

                <label class="code-label" for="totp-code">Authenticator code</label>
                <input
                    id="totp-code"
                    class="code-input"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    pattern="[0-9]*"
                    maxlength="6"
                    .value=${this._code}
                    @input=${this._onInput}
                    @keydown=${this._onCodeKeydown}
                    aria-describedby="totp-hint"
                />
                <span id="totp-hint" class="code-hint">
                    Enter the 6-digit code from your authenticator app.
                </span>

                <pk-button
                    slot="secondary"
                    variant="ghost"
                    type="button"
                    @click=${this._onCancelClick}
                >${cancelLabel}</pk-button>
                <pk-button
                    slot="primary"
                    variant="danger"
                    type="button"
                    ?disabled=${!canVerify}
                    @click=${this._onVerifyClick}
                >${verifyLabel}</pk-button>
            </pk-modal>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTotpPrompt.tagName)) {
    customElements.define(PkTotpPrompt.tagName, PkTotpPrompt);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-totp-prompt': PkTotpPrompt;
    }
}
